import { Close, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import ProtectedPermissionView from "../../components/ProtectedPermissionView";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import service from "../../services/users";
import config from "../../utils/config";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function UserList(props: any) {
  const [pageSize, setPageSize] = useState<number>(10);
  const is_backoffice = props.users === "backoffice";
  const [options, setOptions] = useState<any>({
    keyword: "",
    is_backoffice: is_backoffice,
  });
  const notification = useNotification();
  const deleteUser = useMutation("delete-user", service.deleteUser, {
    onSuccess: () => {
      listUser.refetch();
      notification.onOpen({
        message: "User was successfully deleted!",
        type: "success",
        position: "top",
      });
    },
    onError: (error: any) => {
      listUser.refetch();
      notification.onOpen({
        message:
          error?.response?.data?.message === undefined
            ? "Something went wrong"
            : error?.response?.data?.message,
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });
  const onDelete = (id: number) => {
    alert.onClose();
    console.log("DELETE");
    deleteUser.mutate({
      id: id,
    });
  };
  const alert = useAlert();
  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure want to delete this user?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };
  const listUser = useQuery(
    ["userlist", options, is_backoffice],
    ({ queryKey }) => {
      return service.getUsers({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  useEffect(() => {
    setOptions((prev: any) => ({
      ...prev,
      is_backoffice: is_backoffice,
    }));
  }, [is_backoffice])

  const form = useForm({
    defaultValues: {
      keyword: "",
      is_backoffice: is_backoffice,
    },
  });

  const onHandleSearch = useCallback((values: { keyword: any }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
    }));
  }, []);
  return (
    <MainLayout>
      <Helmet>
        <title>User List</title>
      </Helmet>

      <Breadcrumb
        label={`User List ${is_backoffice ? "Backoffice" : "Tenant"}`}
        breadcrumbs={[
          { label: "User Management", href: "/app/users/backoffice" },
        ]}
      />

      <ProtectedPermissionPage
        requiredPermissions={{ is_read: true }}
        menuSlug={is_backoffice ? "backoffice" : "tenant"}
      >
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <Controller
              name="keyword"
              control={form.control}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Input Keyword here"
                  {...field}
                  {...config.onEnter(() => {
                    form.handleSubmit(onHandleSearch)();
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        onClick={() => {
                          if (field.value) {
                            field.onChange("");
                            setOptions((prev: any) => ({
                              ...prev,
                              keyword: "",
                            }));
                          }
                        }}
                      >
                        {field.value ? (
                          <Close color="error" sx={{ cursor: "pointer" }} />
                        ) : (
                          <Search />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button
              variant="outlined"
              onClick={form.handleSubmit(onHandleSearch)}
            >
              SEARCH
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>

      {is_backoffice && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1}>
            <ProtectedPermissionView
              requiredPermissions={{ is_create: true }}
              menuSlug="backoffice"
            >
              <Button
                variant="contained"
                component={RouterLink}
                to="/app/users/create-user"
              >
                ADD USER
              </Button>
            </ProtectedPermissionView>
          </Stack>
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        <DataGrid
          autoHeight
          loading={listUser.isLoading}
          getRowId={(record) => get(record, "id")}
          rows={get(listUser, "data.data", [])}
          rowCount={get(listUser, "data.data", []).length}
          page={options.page_number}
          pageSize={pageSize}
          rowsPerPageOptions={[10, 20, 30]}
          pagination
          disableSelectionOnClick
          onPageChange={(newPage) => {
            setOptions((prev: any) => ({ ...prev, page_number: newPage }));
          }}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          components={{
            NoRowsOverlay: CustomNoRowOverlay,
          }}
          columns={[
            {
              field: "id",
              headerName: "ID",
              flex: 1,
              filterable: false,
            },
            {
              field: "username",
              headerName: "Username",
              flex: 1,
              filterable: false,
              renderCell: (params) => {
                return (
                  <Stack>
                    <Typography>{params.value}</Typography>
                  </Stack>
                );
              },
            },
            {
              field: "name",
              headerName: "Name",
              flex: 1,
              filterable: true,
              renderCell: (params) => {
                return (
                  <Stack>
                    <Typography>{params.value}</Typography>
                  </Stack>
                );
              },
            },
            {
              field: "email",
              headerName: "Email",
              flex: 1,
              filterable: true,
              renderCell: (params) => {
                return (
                  <Stack>
                    <Typography>{params.value}</Typography>
                  </Stack>
                );
              },
            },
            {
              field: "role",
              headerName: "Role",
              flex: 1,
              filterable: true,
              renderCell: (params) => {
                return (
                  <Stack>
                    <Typography>{params.value}</Typography>
                  </Stack>
                );
              },
            },
            // {
            //   field: "is_active",
            //   headerName: "Is Active",
            //   flex: 1,
            //   filterable: false,
            //   minWidth: 150,
            //   renderCell: (params) => {
            //     return params.value === true ? "Active" : "Not-Active";
            //   },
            // },
            {
              field: "actions",
              headerName: "Action",
              sortable: false,
              filterable: false,
              width: 200,
              renderCell: (params) => {
                return (
                  <Stack direction="row" spacing={1}>
                    <ProtectedPermissionView
                      requiredPermissions={{ is_update: true }}
                      menuSlug={is_backoffice ? "backoffice" : "tenant"}
                    >
                      <Button
                        variant="outlined"
                        color="primary"
                        component={RouterLink}
                        to={`/app/users/edit/${get(params, "row.id")}`}
                      >
                        Edit
                      </Button>
                    </ProtectedPermissionView>
                    <ProtectedPermissionView
                      requiredPermissions={{ is_delete: true }}
                      menuSlug={is_backoffice ? "backoffice" : "tenant"}
                    >
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => onClickDelete(params.row.id)}
                      >
                        Delete
                      </Button>
                    </ProtectedPermissionView>
                  </Stack>
                );
              },
            },
          ]}
          sx={{
            ".MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
            },
          }}
        />
      </Box>
    </MainLayout>
  );
}
